import React, { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  CardContent, 
  Typography, 
  Button,
  Stack,
  TextField,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Gavel as AppealIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { LetterEditor, PolicyReferencesPanel, ClaimContextPanel } from '../components';
import { 
  useGetAppealLetterQuery,
  useSaveAppealLetterMutation,
  useExportAppealPdfMutation,
  setCurrentAppeal,
  updateLetterDraft,
  updateAppealSettings,
  markAsSaved,
  AppealStatus,
  LetterDraftView
} from '../../../services/AppealsService';

const STATUS_COLORS: Record<AppealStatus, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  Draft: 'default',
  Generated: 'info',
  Reviewed: 'primary',
  Approved: 'success',
  Sent: 'secondary',
  Pending: 'warning',
  Won: 'success',
  Lost: 'error',
  Withdrawn: 'default',
};

const AppealEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';

  const { data: appeal, isLoading, error, refetch } = useGetAppealLetterQuery(id!, { skip: !id });
  const [saveAppeal, { isLoading: isSaving }] = useSaveAppealLetterMutation();
  const [exportPdf, { isLoading: isExporting }] = useExportAppealPdfMutation();

  const currentAppeal = useSelector((state: any) => state.appeal?.currentAppeal);
  const hasUnsavedChanges = useSelector((state: any) => state.appeal?.hasUnsavedChanges);

  useEffect(() => {
    if (appeal) {
      dispatch(setCurrentAppeal(appeal));
    }
  }, [appeal, dispatch]);

  const handleSave = async () => {
    if (!currentAppeal) return;

    try {
      await saveAppeal({
        id: currentAppeal.id,
        encounterId: currentAppeal.encounterId,
        accountId: currentAppeal.accountId,
        templateType: currentAppeal.templateType,
        appealType: currentAppeal.appealType,
        status: currentAppeal.status,
        letterDraft: {
          subject: currentAppeal.letterDraft.subject,
          salutation: currentAppeal.letterDraft.salutation,
          openingParagraph: currentAppeal.letterDraft.openingParagraph,
          body: currentAppeal.letterDraft.body,
          medicalNecessityArgument: currentAppeal.letterDraft.medicalNecessityArgument,
          policyComplianceArgument: currentAppeal.letterDraft.policyComplianceArgument,
          supportingDocumentation: currentAppeal.letterDraft.supportingDocumentation,
          closingParagraph: currentAppeal.letterDraft.closingParagraph,
          requestedAction: currentAppeal.letterDraft.requestedAction,
          signature: currentAppeal.letterDraft.signature,
        },
        policyReferenceIds: currentAppeal.policyReferences?.map((p: any) => p.id),
      }).unwrap();
      dispatch(markAsSaved());
    } catch (err) {
      console.error('Failed to save appeal:', err);
    }
  };

  const handleExportPdf = async () => {
    if (!currentAppeal?.id) return;

    try {
      const result = await exportPdf({ id: currentAppeal.id, includeAttachments: true }).unwrap();
      // Open PDF in new tab
      window.open(result.pdfUrl, '_blank');
    } catch (err) {
      console.error('Failed to export PDF:', err);
    }
  };

  const handleStatusChange = (newStatus: AppealStatus) => {
    dispatch(updateAppealSettings({ status: newStatus }));
  };

  const handleBackToWorklist = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    navigate(`/appeals?encounterId=${currentAppeal?.encounterId}&company=${company}&center=${center}`);
  };

  if (!id) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard sx={{ maxWidth: 800 }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                Missing appeal ID. Please select an appeal to edit.
              </Typography>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard sx={{ maxWidth: 800 }}>
            <CardContent sx={{ p: 3 }}>
              <Alert severity="error">
                Failed to load appeal. Please try again.
              </Alert>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard sx={{ maxWidth: 1400, width: '100%' }}>
          {/* Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AppealIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ fontWeight: 600, color: '#1e293b' }}
                  >
                    Edit Appeal Letter
                  </Typography>
                  {currentAppeal && (
                    <Chip 
                      label={currentAppeal.status}
                      color={STATUS_COLORS[currentAppeal.status as AppealStatus]}
                      size="small"
                    />
                  )}
                  {hasUnsavedChanges && (
                    <Chip label="Unsaved" color="warning" size="small" variant="outlined" />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {company} | {center} | {currentAppeal?.claimContext?.patientName}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={handleBackToWorklist}
                sx={{ borderColor: '#64748b', color: '#64748b' }}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPdf}
                disabled={isExporting || !currentAppeal?.id}
                sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
              >
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                sx={{ backgroundColor: '#3b82f6' }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
              {/* Left Column - Letter Editor */}
              <Box sx={{ flex: 2 }}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1e293b' }}>
                    <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Appeal Letter
                  </Typography>
                  {currentAppeal?.letterDraft && (
                    <LetterEditor 
                      letterDraft={currentAppeal.letterDraft}
                      onChange={(updates: Partial<LetterDraftView>) => dispatch(updateLetterDraft(updates))}
                    />
                  )}
                </Paper>
              </Box>

              {/* Right Column - Context and References */}
              <Box sx={{ flex: 1 }}>
                <Stack spacing={2}>
                  {/* Status Actions */}
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b' }}>
                      Update Status
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {['Draft', 'Reviewed', 'Approved', 'Sent'].map((status) => (
                        <Chip
                          key={status}
                          label={status}
                          color={currentAppeal?.status === status ? STATUS_COLORS[status as AppealStatus] : 'default'}
                          variant={currentAppeal?.status === status ? 'filled' : 'outlined'}
                          onClick={() => handleStatusChange(status as AppealStatus)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Paper>

                  {/* Claim Context */}
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b' }}>
                      Claim Context
                    </Typography>
                    <ClaimContextPanel 
                      encounterId={currentAppeal?.encounterId || ''} 
                      accountId={currentAppeal?.accountId || ''} 
                      claimContext={currentAppeal?.claimContext}
                    />
                  </Paper>

                  {/* Policy References */}
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b' }}>
                      Policy References
                    </Typography>
                    <PolicyReferencesPanel 
                      references={currentAppeal?.policyReferences || []}
                    />
                  </Paper>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default AppealEdit;
